/**
 * Independent House Interiors — landing page content
 * Page: /independent-house-interiors-bangalore (footer link + QR posters; not in the header)
 *
 * Keyword targets (from Google autocomplete, India):
 *   core:      independent house interior design · interior design for independent house
 *   variants:  individual house · duplex house · G+1 house · villa / bungalow interior design
 *   features:  under staircase storage design · double height living room design
 *   cost:      interior design for independent house cost · house interior cost in Bangalore
 *   local:     interior designers in JP Nagar · Kanakapura Road · Bangalore
 * Used naturally in headings/copy — never stuffed. EIS is a light mention only.
 *
 * TODO(confirm with Willow): STATS figures, FAQ cost range, FAQ typical timeline.
 */

import { COMPANY } from '@/data/company';
import type { SitePhotoData } from '@/data/sitePhotos';
import rendersJson from '@/data/independentRenders.json';

// ─── 3D DESIGNS (renders) ─────────────────────────────────────────────────────
// Renders already carry Willow's watermark + unit-address strip — nothing added on top.
// They are ~960px wide, so thumbnails stay small and the lightbox caps them at their real size.
// All renders are public (user decision 10-05); data in independentRenders.json, regenerated
// from N:\Willow_photos_inbox\independent-homes by the scratchpad ih-rebuild.ps1 script.

export const IH_CREDIT = {
  designer: 'Ar. Ujwal Prabhu N',
  studio: 'Nord Studio',
  line: 'Design: Ar. Ujwal Prabhu N, Nord Studio — in collaboration with Willow Interiors',
};

const ALT_SUFFIX = ' — 3D interior design for an independent house in Bangalore';
const RENDERS = rendersJson as Record<string, { src: string; alt: string; width: number; height: number }[]>;
const render = (project: string, i: number): SitePhotoData => {
  const p = RENDERS[project][i];
  return { ...p, alt: p.alt + ALT_SUFFIX };
};

/** All renders, grouped exactly as the designer's project folders (Project 1–5, folder order). */
export const IH_DESIGN_PROJECTS = Object.keys(RENDERS).map((key, n) => ({
  id: key,
  title: `Project ${n + 1}`, // replace with real project names once details are supplied
  photos: RENDERS[key].map((_, i) => render(key, i)),
}));

/** Renders used individually on the page (indexes follow independentRenders.json) */
export const IH_RENDERS = {
  hero: render('project-2', 0), // double-height living room, backlit feature wall
  underStairStorage: render('project-1', 1), // dining beside the staircase, storage under it
  underStairPooja: render('project-5', 2), // pooja + indoor garden under the staircase
  doubleHeight: render('project-5', 6), // duplex double-height space with a bridge
};

export const IH_PAGE = {
  slug: '/independent-house-interiors-bangalore',
  title: 'Independent House Interior Design in Bangalore | Willow Interiors',
  description:
    'Interior design for independent houses, duplexes, G+1 homes and villas in Bangalore — staircases, double-height spaces and phase-wise budgets, built in our own unit in JP Nagar.',
};

export const IH_HERO = {
  label: 'INDEPENDENT HOUSES · BANGALORE',
  h1: 'Interiors for Independent Houses in Bangalore',
  subline: 'Designed for the way a house is lived in — and for the budget left after building it.',
  intro:
    'Independent house interior design is a different job from designing a flat. We design and build interiors for independent houses, duplexes, G+1 homes and villas across Bangalore, from our own manufacturing unit in JP Nagar, off Kanakapura Road.',
  whatsapp: `https://wa.me/${COMPANY.whatsapp.number}?text=${encodeURIComponent(
    'Hi Willow, I’m building an independent house and would like to plan the interiors.'
  )}`,
};

export const IH_DIFFERENT = {
  label: 'WHY IT’S DIFFERENT',
  h2: 'A house is not a big flat.',
  intro:
    'Interior design for a 1,200 sq ft flat and for a 1,200 sq ft two-storey independent house are two different jobs. A house brings things a flat never has:',
  points: [
    { title: 'Staircases', text: 'and the under-staircase storage designed into them' },
    { title: 'Double-height spaces', text: 'living rooms and higher ceilings that change how a room is designed and lit' },
    { title: 'Multiple floors', text: 'each with its own purpose and its own way of being used' },
    { title: 'Spacious rooms', text: 'that need to feel warm and lived-in, not empty' },
    { title: 'More of everything', text: 'bathrooms, balconies, a pooja room and utility spaces' },
  ],
  closing:
    'Each needs its own design language — and usually a larger interior budget. We plan for that from day one.',
};

export const IH_APPROACH = {
  label: 'OUR APPROACH',
  h2: 'Design that fits your house — and your budget.',
  body:
    'Our designers — and design consultants who specialise in independent houses — plan every design knowing how it will be built. We show you what to do now and what can wait.',
  points: [
    { title: 'Deadlines we keep', text: 'Planned production and installation schedules.' },
    { title: 'Interiors that last longer', text: 'Built in our own unit to Ennezz Interior Standards (EIS).' },
    { title: 'Modular where it makes sense', text: 'Custom where the house needs it.' },
  ],
};

// TODO(confirm with Willow): exact figures / whether "+" is right
export const IH_STATS = [
  { value: '150+', label: 'Independent homes designed and built' },
  { value: '50+', label: 'Architects we’ve worked alongside' },
  { value: 'JP Nagar', label: 'Our own manufacturing unit, Bangalore' },
];

export const IH_FAQS = [
  {
    question: 'How much does interior design cost for an independent house in Bangalore?',
    // TODO(confirm with Willow): add a real range (per sq ft or per house) — specific figures are what Google and AI assistants quote
    answer:
      'It depends on the built-up area, the number of floors and rooms, and the finishes you choose. An independent house usually needs a larger interior budget than a flat of the same area — staircases, higher ceilings and more rooms all add to it. We share an itemised quotation and show you which parts can be done now and which can be phased for later.',
  },
  {
    question: 'How is independent house interior design different from a flat?',
    answer:
      'A house brings spaces a flat never has — staircases and under-staircase storage, double-height living rooms, multiple floors, larger rooms, more bathrooms, balconies and a pooja room. Each needs its own design language, so a 1,200 sq ft two-storey house and a 1,200 sq ft flat are designed very differently.',
  },
  {
    question: 'Can we do the interiors of our house in phases?',
    answer:
      'Yes. After building a house, budgets are often tight, so we plan the interiors in priority order — what you need to move in now, and what can wait for later — without compromising the overall design.',
  },
  {
    question: 'How long do interiors for an independent house take?',
    // TODO(confirm with Willow): add a typical timeline in weeks
    answer:
      'The timeline depends on the size of the house and the scope of work. We agree a production and installation schedule before work begins and plan to keep to it — our own manufacturing unit means we are not waiting on outside workshops.',
  },
  {
    question: 'Can you work with my architect’s design?',
    answer:
      'Yes. We regularly work alongside architects and independent design consultants, turning their designs into interiors that are made and installed as drawn.',
  },
  {
    question: 'Do you manufacture the interiors yourselves?',
    answer:
      'Yes. Kitchens, wardrobes, TV units and other furniture are made in our own manufacturing unit in JP Nagar, Bangalore, built to our in-house Ennezz Interior Standards (EIS) for interiors that last longer.',
  },
];
